import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Form } from "react-bootstrap";
import Layout from "../../layout/Layout";
import { getAylikAnaliz } from "../../../api/uretim/makineDataApi";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement
);

function AylikBoruProfilAnaliz() {
  const [data, setData] = useState([]);
  const now = new Date();

  const aylar = [
    "Ocak",
    "Şubat",
    "Mart",
    "Nisan",
    "Mayıs",
    "Haziran",
    "Temmuz",
    "Ağustos",
    "Eylül",
    "Ekim",
    "Kasım",
    "Aralık",
  ];

  const [year, setYear] = useState(String(now.getFullYear()));

  // Sayfa ilk açıldığında mevcut ay seçili gelir.
  const [month, setMonth] = useState(aylar[now.getMonth()]);

  const [loading, setLoading] = useState(false);
  const [yillik, setYillik] = useState(null);

  const durusKeys = [
    "akumulasyon",
    "yig",
    "form",
    "kaynak",
    "elektrikKesintisi",
    "elektrikArizasi",
    "galvanizUnitesi",
    "kalibreTurkKafalari",
    "yaglama",
    "rulmanDegisimi",
    "testereArizasi",
    "makaraDegisimi",
    "ebatAyari",
    "istif",
    "isEmriYok",
    "personelEksikligi",
    "yuklemeYapilmasi",
    "kalipDegisimi",
    "testereDegisimi",
    "toplantiEgitim",
    "servisGecGelmesi",
    "vincBekleme",
    "digerSebepler",
    "yemekCayMolasi",
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const res = await getAylikAnaliz(year);

      if (res.data?.data?.aylik) {
        setData(res.data.data.aylik);
      } else {
        setData([]);
      }

      if (res.data?.data?.yillik) {
        setYillik(res.data.data.yillik);
      } else {
        setYillik(null);
      }
    } catch (err) {
      console.error("Veri çekme hatası:", err);
      setData([]);
      setYillik(null);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sortedData = [...data].sort(
    (a, b) => aylar.indexOf(a.ay) - aylar.indexOf(b.ay)
  );

  const visibleMonths =
    month === "Tüm Aylar"
      ? sortedData
      : sortedData.filter((item) => item.ay === month);

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString("tr-TR", {
      maximumFractionDigits: 2,
    });
  };

  const formatPercent = (value) => {
    return Number(value || 0).toFixed(2);
  };

  // Grafik verilerindeki uzun küsuratları temizler.
  const cleanChartData = (values = [], decimalDigits = 0) => {
    return values.map((value) => {
      const number = Number(value);

      if (!Number.isFinite(number)) {
        return 0;
      }

      return Number(number.toFixed(decimalDigits));
    });
  };

  // Grafik ekseni ve tooltip değerlerini Türkçe sayı formatında gösterir.
  const formatChartNumber = (value, decimalDigits = 0) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "0";
    }

    return number.toLocaleString("tr-TR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimalDigits,
    });
  };

  const getChartOptions = ({
    decimalDigits = 0,
    suffix = "",
    beginAtZero = true,
  } = {}) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label
              ? `${context.dataset.label}: `
              : "";

            return `${datasetLabel}${formatChartNumber(
              context.parsed.y,
              decimalDigits
            )}${suffix}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero,
        ticks: {
          callback: (value) =>
            `${formatChartNumber(value, decimalDigits)}${suffix}`,
        },
      },
    },
  });

  const getMonthTotals = (makineler = []) => {
    const totals = {
      makinaAdi: "GENEL TOPLAM",

      toplamUretim: 0,
      toplamAlternatif: 0,
      toplamIskarta: 0,
      toplamGenelKg: 0,
      toplamMetraj: 0,

      aStok: 0,
      iskartaYuzde: 0,
      altStokIskarta: 0,

      durusToplam: 0,
      fiiliCalismaSuresi: 0,
      toplamMakinaKullanimSuresi: 0,

      verim: 0,
      vardiyaSayisi: 0,
      tonVardiya: 0,
      kisiBasiTonVardiya: 0,

      durusKalemleri: {},
    };

    durusKeys.forEach((key) => {
      totals.durusKalemleri[key] = 0;
    });

    makineler.forEach((m) => {
      totals.toplamUretim += Number(m.toplamUretim) || 0;
      totals.toplamAlternatif += Number(m.toplamAlternatif) || 0;
      totals.toplamIskarta += Number(m.toplamIskarta) || 0;
      totals.toplamGenelKg += Number(m.toplamGenelKg) || 0;
      totals.toplamMetraj += Number(m.toplamMetraj) || 0;

      totals.durusToplam += Number(m.durusToplam) || 0;
      totals.fiiliCalismaSuresi += Number(m.fiiliCalismaSuresi) || 0;
      totals.toplamMakinaKullanimSuresi +=
        Number(m.toplamMakinaKullanimSuresi) || 0;

      durusKeys.forEach((key) => {
        totals.durusKalemleri[key] += Number(m.durusKalemleri?.[key]) || 0;
      });
    });

    if (totals.toplamUretim > 0) {
      totals.aStok = Number(
        ((totals.toplamAlternatif * 100) / totals.toplamUretim).toFixed(2)
      );

      totals.iskartaYuzde = Number(
        ((totals.toplamIskarta * 100) / totals.toplamUretim).toFixed(2)
      );

      totals.altStokIskarta = Number(
        (totals.aStok + totals.iskartaYuzde).toFixed(2)
      );
    }

    if (totals.toplamMakinaKullanimSuresi > 0) {
      totals.verim = Number(
        (
          (totals.fiiliCalismaSuresi * 100) /
          totals.toplamMakinaKullanimSuresi
        ).toFixed(2)
      );

      totals.vardiyaSayisi = Number(
        (totals.toplamMakinaKullanimSuresi / 450).toFixed(2)
      );

      totals.tonVardiya = Number(
        (totals.toplamUretim / (totals.vardiyaSayisi || 1) / 1000).toFixed(2)
      );

      totals.kisiBasiTonVardiya = Number((totals.tonVardiya / 4).toFixed(2));
    }

    return totals;
  };

  const renderMachineRow = (m, rowKey, isTotal = false) => {
    return (
      <tr
        key={rowKey}
        className={`text-center ${isTotal ? "table-secondary fw-bold" : ""}`}
      >
        <td className="fw-bold">{m.makinaAdi}</td>

        <td>{formatNumber(m.toplamUretim)}</td>
        <td>{formatNumber(m.toplamAlternatif)}</td>
        <td>{formatNumber(m.toplamIskarta)}</td>
        <td>{formatNumber(m.toplamGenelKg)}</td>
        <td>{formatNumber(m.toplamMetraj)}</td>

        <td>{formatPercent(m.aStok)}</td>
        <td>{formatPercent(m.iskartaYuzde)}</td>
        <td>{formatPercent(m.altStokIskarta)}</td>

        <td>{formatNumber(m.durusToplam)}</td>
        <td>{formatNumber(m.fiiliCalismaSuresi)}</td>
        <td>{formatNumber(m.toplamMakinaKullanimSuresi)}</td>

        <td>{formatPercent(m.verim)}%</td>
        <td>{formatNumber(m.vardiyaSayisi)}</td>
        <td>{formatNumber(m.tonVardiya)}</td>
        <td>{formatNumber(m.kisiBasiTonVardiya)}</td>

        <td>{formatNumber(m.durusKalemleri?.akumulasyon)}</td>
        <td>{formatNumber(m.durusKalemleri?.yig)}</td>
        <td>{formatNumber(m.durusKalemleri?.form)}</td>
        <td>{formatNumber(m.durusKalemleri?.kaynak)}</td>
        <td>{formatNumber(m.durusKalemleri?.elektrikKesintisi)}</td>
        <td>{formatNumber(m.durusKalemleri?.elektrikArizasi)}</td>
        <td>{formatNumber(m.durusKalemleri?.galvanizUnitesi)}</td>
        <td>{formatNumber(m.durusKalemleri?.kalibreTurkKafalari)}</td>
        <td>{formatNumber(m.durusKalemleri?.yaglama)}</td>
        <td>{formatNumber(m.durusKalemleri?.rulmanDegisimi)}</td>
        <td>{formatNumber(m.durusKalemleri?.testereArizasi)}</td>
        <td>{formatNumber(m.durusKalemleri?.makaraDegisimi)}</td>
        <td>{formatNumber(m.durusKalemleri?.ebatAyari)}</td>
        <td>{formatNumber(m.durusKalemleri?.istif)}</td>
        <td>{formatNumber(m.durusKalemleri?.isEmriYok)}</td>
        <td>{formatNumber(m.durusKalemleri?.personelEksikligi)}</td>
        <td>{formatNumber(m.durusKalemleri?.yuklemeYapilmasi)}</td>
        <td>{formatNumber(m.durusKalemleri?.kalipDegisimi)}</td>
        <td>{formatNumber(m.durusKalemleri?.testereDegisimi)}</td>
        <td>{formatNumber(m.durusKalemleri?.toplantiEgitim)}</td>
        <td>{formatNumber(m.durusKalemleri?.servisGecGelmesi)}</td>
        <td>{formatNumber(m.durusKalemleri?.vincBekleme)}</td>
        <td>{formatNumber(m.durusKalemleri?.digerSebepler)}</td>
        <td>{formatNumber(m.durusKalemleri?.yemekCayMolasi)}</td>
      </tr>
    );
  };

  const renderMonthTable = (monthData) => {
    const monthTotals = getMonthTotals(monthData.makineler);

    return (
      <div key={monthData.ay} className="mb-5">
        <h4 className="fw-bold mb-3">{monthData.ay} Ayı Makine Analizi</h4>

        <div className="table-responsive" style={{ maxHeight: "75vh" }}>
          <Table bordered hover size="sm">
            <thead className="table-dark text-center">
              <tr>
                <th rowSpan="2">Makine</th>

                <th colSpan="5">ÜRETİM</th>
                <th colSpan="3">ORANLAR (%)</th>
                <th colSpan="3">SÜRELER (dk)</th>
                <th colSpan="4">KPI</th>
                <th colSpan="24">DURUŞ KALEMLERİ</th>
              </tr>

              <tr>
                <th>ÜRETİM MİKTARI (kg)</th>
                <th>ALTERNATİF MİKTARI (kg)</th>
                <th>ISKARTA MİKTARI (kg)</th>
                <th>TOPLAM ÜRETİM (kg)</th>
                <th>METRAJ (mm)</th>

                <th>A.STOK (%)</th>
                <th>ISKARTA (%)</th>
                <th>ALT.STOK + ISKARTA</th>

                <th>DURUŞ SÜRESİ (dk)</th>
                <th>FİİLİ ÇALIŞMA (dk)</th>
                <th>TOPLAM MAKİNA KULLANIM (dk)</th>

                <th>VERİM (%)</th>
                <th>VARDİYA SAYISI</th>
                <th>(ton/vardiya)</th>
                <th>Kişi başı üretim (ton/vardiya)</th>

                <th>AKÜMÜLASYON</th>
                <th>YIĞMA</th>
                <th>FORM</th>
                <th>KAYNAK</th>
                <th>E.KESİNTİSİ</th>
                <th>E.ARIZASI</th>
                <th>GALVANİZ ÜNİTESİ</th>
                <th>KALİBRE + TÜRK KAFALARI</th>
                <th>YAĞLAMA</th>
                <th>RULMAN DEĞİŞİMİ</th>
                <th>TESTERE ARIZASI</th>
                <th>MAKARA DEĞİŞİMİ</th>
                <th>EBAT AYARI</th>
                <th>İSTİF</th>
                <th>İŞ EMRİ YOK</th>
                <th>PERSONEL EKSİKLİĞİ</th>
                <th>YÜKLEME YAPILMASI</th>
                <th>KALIP DEĞİŞİMİ</th>
                <th>TESTERE DEĞİŞİMİ</th>
                <th>TOPLANTI - EĞİTİM</th>
                <th>SERVİS GEÇ GELMESİ</th>
                <th>VİNÇ BEKLEME</th>
                <th>DİĞER SEBEPLER</th>
                <th>YEMEK + ÇAY</th>
              </tr>
            </thead>

            <tbody>
              {monthData.makineler.map((m, idx) =>
                renderMachineRow(
                  m,
                  `${monthData.ay}-${m.makinaAdi}-${idx}`,
                  false
                )
              )}

              {renderMachineRow(
                monthTotals,
                `${monthData.ay}-genel-toplam`,
                true
              )}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="container mt-3">
        <h3 className="fw-bold mb-3">Makine Süre Analiz</h3>

        <div className="d-flex gap-3 mb-4">
          <Form.Select
            style={{ width: "200px" }}
            value={year}
            onChange={(e) => {
              setYear(e.target.value);

              if (e.target.value === String(now.getFullYear())) {
                setMonth(aylar[now.getMonth()]);
              } else {
                setMonth("Tüm Aylar");
              }
            }}
          >
            <option value="2026">2026</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
            <option value="2023">2023</option>
          </Form.Select>

          <Form.Select
            style={{ width: "200px" }}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            <option>Tüm Aylar</option>
            <option>Ocak</option>
            <option>Şubat</option>
            <option>Mart</option>
            <option>Nisan</option>
            <option>Mayıs</option>
            <option>Haziran</option>
            <option>Temmuz</option>
            <option>Ağustos</option>
            <option>Eylül</option>
            <option>Ekim</option>
            <option>Kasım</option>
            <option>Aralık</option>
          </Form.Select>
        </div>

        {loading && (
          <div className="text-center my-5">
            <Spinner animation="border" />
          </div>
        )}

        {!loading && visibleMonths.length === 0 && (
          <div className="alert alert-warning">
            Gösterilecek veri bulunamadı.
          </div>
        )}

        {!loading && visibleMonths.length > 0 && (
          <>{visibleMonths.map((monthData) => renderMonthTable(monthData))}</>
        )}

        {yillik && (
          <div className="mt-5">
            <h4 className="fw-bold mb-4 text-center">
              Yıllık Makine Karşılaştırma Grafikleri
            </h4>

            <div className="mb-5 p-3 border rounded bg-white">
              <h5 className="fw-bold text-center">
                Yıllık Üretim Karşılaştırması (kg)
              </h5>

              <div style={{ height: "430px" }}>
                <Bar
                  data={{
                    labels: yillik.aylar,
                    datasets: [
                      {
                        label: "Makine 1",
                        data: cleanChartData(yillik.mak1?.uretim, 0),
                        backgroundColor: "rgba(255, 99, 132, 0.6)",
                      },
                      {
                        label: "Makine 2",
                        data: cleanChartData(yillik.mak2?.uretim, 0),
                        backgroundColor: "rgba(54, 162, 235, 0.6)",
                      },
                      {
                        label: "Makine 3",
                        data: cleanChartData(yillik.mak3?.uretim, 0),
                        backgroundColor: "rgba(255, 206, 86, 0.6)",
                      },
                      {
                        label: "Makine 4",
                        data: cleanChartData(yillik.mak4?.uretim, 0),
                        backgroundColor: "rgba(75, 192, 192, 0.6)",
                      },
                    ],
                  }}
                  options={getChartOptions({
                    decimalDigits: 0,
                  })}
                />
              </div>
            </div>

            <div className="mb-5 p-3 border rounded bg-white">
              <h5 className="fw-bold text-center">
                Yıllık Toplam Duruş Süresi (dk)
              </h5>

              <div style={{ height: "430px" }}>
                <Bar
                  data={{
                    labels: yillik.aylar,
                    datasets: [
                      {
                        label: "Makine 1",
                        data: cleanChartData(yillik.mak1?.durus, 0),
                        backgroundColor: "rgba(255, 159, 64, 0.6)",
                      },
                      {
                        label: "Makine 2",
                        data: cleanChartData(yillik.mak2?.durus, 0),
                        backgroundColor: "rgba(153, 102, 255, 0.6)",
                      },
                      {
                        label: "Makine 3",
                        data: cleanChartData(yillik.mak3?.durus, 0),
                        backgroundColor: "rgba(255, 99, 132, 0.6)",
                      },
                      {
                        label: "Makine 4",
                        data: cleanChartData(yillik.mak4?.durus, 0),
                        backgroundColor: "rgba(75, 192, 192, 0.6)",
                      },
                    ],
                  }}
                  options={getChartOptions({
                    decimalDigits: 0,
                  })}
                />
              </div>
            </div>

            <div className="mb-5 p-3 border rounded bg-white">
              <h5 className="fw-bold text-center">Yıllık Verim (%)</h5>

              <div style={{ height: "430px" }}>
                <Line
                  data={{
                    labels: yillik.aylar,
                    datasets: [
                      {
                        label: "Makine 1",
                        data: cleanChartData(yillik.mak1?.verim, 2),
                        borderColor: "rgba(255, 99, 132, 1)",
                        tension: 0.25,
                      },
                      {
                        label: "Makine 2",
                        data: cleanChartData(yillik.mak2?.verim, 2),
                        borderColor: "rgba(54, 162, 235, 1)",
                        tension: 0.25,
                      },
                      {
                        label: "Makine 3",
                        data: cleanChartData(yillik.mak3?.verim, 2),
                        borderColor: "rgba(255, 206, 86, 1)",
                        tension: 0.25,
                      },
                      {
                        label: "Makine 4",
                        data: cleanChartData(yillik.mak4?.verim, 2),
                        borderColor: "rgba(75, 192, 192, 1)",
                        tension: 0.25,
                      },
                    ],
                  }}
                  options={getChartOptions({
                    decimalDigits: 2,
                    suffix: "%",
                  })}
                />
              </div>
            </div>

            <div className="mb-5 p-3 border rounded bg-white">
              <h5 className="fw-bold text-center">Yıllık Metraj (mm)</h5>

              <div style={{ height: "430px" }}>
                <Line
                  data={{
                    labels: yillik.aylar,
                    datasets: [
                      {
                        label: "Makine 1",
                        data: cleanChartData(yillik.mak1?.metraj, 0),
                        borderColor: "rgba(255, 99, 132, 1)",
                        tension: 0.25,
                      },
                      {
                        label: "Makine 2",
                        data: cleanChartData(yillik.mak2?.metraj, 0),
                        borderColor: "rgba(54, 162, 235, 1)",
                        tension: 0.25,
                      },
                      {
                        label: "Makine 3",
                        data: cleanChartData(yillik.mak3?.metraj, 0),
                        borderColor: "rgba(255, 206, 86, 1)",
                        tension: 0.25,
                      },
                      {
                        label: "Makine 4",
                        data: cleanChartData(yillik.mak4?.metraj, 0),
                        borderColor: "rgba(75, 192, 192, 1)",
                        tension: 0.25,
                      },
                    ],
                  }}
                  options={getChartOptions({
                    decimalDigits: 0,
                  })}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default AylikBoruProfilAnaliz;
